import { faInfoCircle, faStar } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  HeadlineXL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { ReinsuranceItem } from '#src/components/fiatWallet/CreateFiatWallet/Onboarding/ReinsuranceItem/index.tsx';
import { StaticCashBalance } from '#src/components/fiatWallet/CreateFiatWallet/Onboarding/StaticCashBalance/index.tsx';
import {
  LEARN_MORE_ABOUT_FIAT_WALLET,
  NOT_SUPPORTED_COUNTRIES_LINK,
} from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { fiatWallet, glossary } from '#src/lib/glossary.ts';

const Wrapper = styled(Vertical).attrs({ gap: 4, center: true })`
  padding-top: var(--double-unit);
  justify-content: flex-start;
`;

const Actions = styled(Vertical).attrs({ center: true })`
  width: 100%;
  justify-content: flex-start;
`;

const News = styled(Vertical)`
  align-items: stretch;
  justify-content: center;
`;

const Helper = styled(Horizontal).attrs({ gap: 2 })`
  color: var(--c-nd-600);
  background-color: var(--c-nd-100);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

type Props = {
  setStep: (tab: CreateFiatWalletSteps) => void;
  onDismiss?: () => void;
  title: ReactNode;
  news: { id: string; title: ReactNode; desc: ReactNode }[];
};

export const Content = ({ title, news, setStep, onDismiss }: Props) => {
  const { canListAndTrade } = useFiatBalance();
  const { cryptocurrencySymbol } = useCurrentUserCurrencies();
  return (
    <Wrapper>
      <StaticCashBalance />
      <HeadlineXL as="h2" className="text-center">
        {title}
      </HeadlineXL>
      {!canListAndTrade && (
        <Helper>
          <FontAwesomeIcon icon={faInfoCircle} />
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              id="walletSetup.whatsNew.helper2"
              defaultMessage="Cards currently listed will continue to be sold for {cryptocurrencySymbol}. After activating your Cash Wallet, you can sell cards for cash or {cryptocurrencySymbol}."
              values={{
                cryptocurrencySymbol,
              }}
            />
          </LabelM>
        </Helper>
      )}
      <News>
        {news.map(({ id, title: newsTitle, desc }) => (
          <ReinsuranceItem
            key={id}
            title={newsTitle}
            desc={desc}
            icon={faStar}
          />
        ))}
      </News>
      <BodyM className="text-center" bold color="var(--c-link)">
        <StyledLink
          href={LEARN_MORE_ABOUT_FIAT_WALLET}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FormattedMessage
            id="walletSetup.whatsNew.learnMoreLink"
            defaultMessage="Learn more about Cash Wallet"
          />
        </StyledLink>
      </BodyM>
      <LabelS color="var(--c-nd-600)">
        <FormattedMessage
          id="walletSetup.whatsNew.disclaimer"
          defaultMessage="Cash Wallet is powered by Mangopay. Availability may vary depending on your country of residence.{br} <link>Learn more</link>"
          values={{
            link: (...chunks) => (
              <StyledLink
                target="_blank"
                href={NOT_SUPPORTED_COUNTRIES_LINK}
                rel="noopener noreferrer"
              >
                {chunks}
              </StyledLink>
            ),
            br: <br />,
          }}
        />
      </LabelS>
      <Actions>
        <Button
          color="primary"
          size="medium"
          fullWidth
          onClick={() => setStep(CreateFiatWalletSteps.INTRO)}
        >
          <FormattedMessage
            {...(canListAndTrade
              ? glossary.continue
              : fiatWallet.activateCashWallet)}
          />
        </Button>
        {!!onDismiss && (
          <Button color="tertiary" size="medium" fullWidth onClick={onDismiss}>
            <FormattedMessage {...glossary.doItLater} />
          </Button>
        )}
      </Actions>
    </Wrapper>
  );
};
